import React from 'react'
import useSWRMutation from 'swr/mutation'
import { Link, useParams } from 'react-router-dom'

import MasterStudioAIPage, { STUDIO_AI_TABS } from '../Master'
import { uploadAndAnalyzeCVForJobOffer } from '../../../../api/cv-roaster'
import Loading from '../../../components/layout/Loading'
import ErrorMessage from '../../../components/errors/ErrorMessage'
import CVResults from '../../../components/cv-roaster/CVResults'
import { useI18N } from '../../../../libraries/i18n/hook'
import type { CVRoasterResult } from '../../../../api/cv-roaster/types'
import CVUploader from '../../../components/cv-roaster/CVUploader'
import AiDisclaimer from '../../../components/ai/Disclaimer'
import { useJobOffer } from '../../../containers/job-offers/hooks'
import Card from '../../../components/layout/Card'
import Button from '../../../components/form/Button'
import { useCVForOffer } from '../../../containers/cv/hooks'
import JobHeader from '../../../components/job-offers/JobHeader'
import { UserCv } from '../../../../api/job-offers/types'

const JobOfferCVRoaster = () => {
  const { t } = useI18N()
  const { id } = useParams<{ id: string }>()
  const [result, setResult] = React.useState<CVRoasterResult | UserCv | null>(null)
  const [isNewVersionInProgress, setNewVersionInProgress] = React.useState<boolean>(false)

  const { data: jobOffer, isLoading: loadingJobOffer, error: jobOfferError } = useJobOffer(id ? parseInt(id) : null)
  const { data, isLoading, error } = useCVForOffer(id && !isNewVersionInProgress ? parseInt(id) : undefined)

  const { trigger: triggerCvRoasterForJobOffer, error: errorCvRoasterForJobOffer, isMutating } = useSWRMutation(`/cv-mathing/job-offers/${id}`,
      uploadAndAnalyzeCVForJobOffer, {
      onSuccess: (data) => {
        setResult(data)
      }
    }
  )

  React.useEffect(() => {
    if (data) {
      setResult(data)
    }
  }, [data])

  const handleUpload = async (file: File) => {
    if (!id) {
      console.error('Job offer ID is undefined')
      return
    }
    await triggerCvRoasterForJobOffer({ file, jobOfferId: parseInt(id) })
    setNewVersionInProgress(false)
  }

  const newCvVersionClick = () => {
    setResult(null)
    setNewVersionInProgress(true)
  }

  // Show loading if job offer is loading or CV data is loading or upload is in progress
  const isPageLoading = loadingJobOffer || isLoading

  return (
    <MasterStudioAIPage activeTab={STUDIO_AI_TABS.CV_ROASTER} buttons={null} goBackLink={`/jobs/${id}/application`}>
      <Card className='w-full pb-12'>
        <div className='max-w-4xl mx-auto'>
          {!loadingJobOffer && jobOffer && <JobHeader heading={t('studioAi.home.cvRoaster.job.feedback')} jobOffer={jobOffer} />}
          {isPageLoading && <Loading />}
          {(error || errorCvRoasterForJobOffer || jobOfferError) && (
            <ErrorMessage message={errorCvRoasterForJobOffer?.message ?? jobOfferError?.message} />
          )}
          {!isPageLoading && (result as CVRoasterResult)?.analysis && (
            <CVResults
              jobOfferId={id ? parseInt(id) : undefined}
              title={t('cv.analysis.results.for.offer')}
              feedbackTitle={t('cv.points.offer.requirements')}
              result={result as CVRoasterResult}
              file={null}
              newButton={{ label: t('studioAi.home.cvRoaster.job.new'), onClick: newCvVersionClick }}
            />
          )}
          {!isPageLoading && !(result as CVRoasterResult)?.analysis && <CVUploader onUpload={handleUpload} isUploading={isMutating} uploadError={errorCvRoasterForJobOffer} />}
          {!isPageLoading && !result && <AiDisclaimer />}
        </div>
      </Card>
      {result && (
        <div className='bg-white w-full shadow-sm shadow-black fixed left-0 py-2 bottom-0 fixed flex justify-center'>
          <Button size='md'><Link to={`/jobs/${id}/application`}>{t('cv.save.new.version')}</Link></Button>
        </div>
      )}
    </MasterStudioAIPage>
  )
}

export default JobOfferCVRoaster
